// Keyboard shortcuts handler

import { getViewer, getToolMode } from './state'
import { toggleWalkMode, toggleLayerVisibility, selectToolMode } from './controls'
import { isEditMode } from '@/utils/editMode'
import { undoStack } from '@/editor/UndoStack'

/**
 * Handle global keyboard shortcuts
 */
export function handleGlobalKeydown(event: KeyboardEvent): void {
  // Ignore if typing in an input
  if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
    return
  }

  const viewer = getViewer()

  // Ctrl/Cmd shortcuts (edit mode only)
  if ((event.ctrlKey || event.metaKey) && isEditMode()) {
    // Ctrl+Shift+S = Save As (duplicate)
    if (event.code === 'KeyS' && event.shiftKey) {
      import('./topBar').then(m => m.handleSaveAsClick())
      event.preventDefault()
      return
    }

    // Ctrl+A/C/V/D = Select All/Copy/Paste/Duplicate (handled by SelectTool, prevent browser default)
    if (event.code === 'KeyA' || event.code === 'KeyC' || event.code === 'KeyV' || event.code === 'KeyD' || event.code === 'KeyL') {
      event.preventDefault()
      return
    }

    // Ctrl+Z = Undo, Ctrl+Shift+Z / Ctrl+Y = Redo
    if (event.code === 'KeyZ' && !event.shiftKey) {
      undoStack.undo()
      event.preventDefault()
      return
    }
    if ((event.code === 'KeyZ' && event.shiftKey) || event.code === 'KeyY') {
      undoStack.redo()
      event.preventDefault()
      return
    }
  }

  // Skip if modifier keys are held (except Shift)
  if (event.ctrlKey || event.metaKey || event.altKey) {
    return
  }

  // F key toggles walk mode (only in 3D)
  if (event.code === 'KeyF') {
    if (viewer?.getViewMode() === '3d') {
      toggleWalkMode()
      event.preventDefault()
    }
    return
  }

  // V key toggles tool mode (pan/select) - only in edit mode
  if (event.code === 'KeyV') {
    if (isEditMode()) {
      const currentMode = getToolMode()
      const newMode = currentMode === 'hand' ? 'pointer' : 'hand'
      selectToolMode(newMode)
      event.preventDefault()
    }
    return
  }

  // R key: activate ruler mode
  if (event.code === 'KeyR' && !event.shiftKey) {
    if (isEditMode()) {
      selectToolMode('ruler')
      event.preventDefault()
    }
    return
  }

  // Layer visibility toggles: 1, 2, 3, 4, 5
  switch (event.key) {
    case '1':
      toggleLayerVisibility('plants')
      event.preventDefault()
      break
    case '2':
      toggleLayerVisibility('buildings')
      event.preventDefault()
      break
    case '3':
      toggleLayerVisibility('ground')
      event.preventDefault()
      break
    case '4':
      toggleLayerVisibility('metadata')
      event.preventDefault()
      break
    case '5':
      toggleLayerVisibility('map')
      event.preventDefault()
      break
  }
}
